import React from 'react';
import {
  Checkbox,
  Content,
  DataList,
  DataListCell,
  DataListCheck,
  DataListItem,
  DataListItemCells,
  DataListItemRow,
  Label,
} from '@patternfly/react-core';
import type { DomainResourceElement, DomainResourceType, DomainResourceTypeKey } from './domainResourceTypes';
import { selectAllState, typeState } from './deleteResourcesWizardSelection';
import type { DeleteResourcesSelection, ResourceInventory, TypeState } from './deleteResourcesWizardSelection';

interface ResourceTypeSelectionStepProps {
  domainName: string;
  types: readonly DomainResourceType[];
  inventory: ResourceInventory;
  selection: DeleteResourcesSelection;
  onToggleType: (key: DomainResourceTypeKey, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
}

const ELEMENT_KIND_LABEL: Record<string, { one: string; many: string }> = {
  mailbox: { one: 'mailbox', many: 'mailboxes' },
  alias: { one: 'alias', many: 'aliases' },
  certificate: { one: 'certificate', many: 'certificates' },
};

/**
 * The one-line summary shown under a type's description: what it actually holds.
 * Atomic types show their record's own detail (record counts, registrar and expiry); per-element
 * types show a count per kind. Pure — exported for the tests.
 */
export const summarizeType = (type: DomainResourceType, elements: readonly DomainResourceElement[]): string | null => {
  if (elements.length === 0) {
    return null;
  }
  if (type.atomic) {
    return elements[0].detail;
  }
  const counts = new Map<string, number>();
  for (const element of elements) {
    if (element.derived) {
      continue;
    }
    counts.set(element.kind, (counts.get(element.kind) ?? 0) + 1);
  }
  if (counts.size === 0) {
    // Only the derived record: a mail domain with no mailboxes and no aliases.
    return 'No mailboxes or aliases';
  }
  return Array.from(counts)
    .map(([kind, count]) => {
      const label = ELEMENT_KIND_LABEL[kind];
      const noun = label === undefined ? kind : count === 1 ? label.one : label.many;
      return `${count} ${noun}`;
    })
    .join(', ');
};

/** The reason a type cannot be selected, as visible text (a disabled checkbox cannot carry a tooltip). */
const blockedNote = (
  state: TypeState,
  type: DomainResourceType,
  elements: readonly DomainResourceElement[],
): string | null => {
  if (state === 'under_development') {
    return `${type.label} deletion is under development.`;
  }
  if (state === 'nothing_hosted') {
    return 'Nothing hosted.';
  }
  if (state === 'all_forced_kept') {
    const reasons = Array.from(
      new Set(elements.map((element) => element.blockedReason).filter((reason): reason is string => reason !== null)),
    );
    return reasons.length > 0 ? reasons.join(' ') : 'Nothing here can be deleted.';
  }
  return null;
};

/**
 * Step 1 of the "Delete resources" wizard: which resource types to delete.
 *
 * Only `selectable` types can be ticked; the other three states render a disabled row whose reason is
 * **visible text** (PatternFly hard-disables the checkbox, so a tooltip would never be reachable).
 * The organization domain itself is asked for in the review step, where the full invariant is
 * evaluable — see `docs/plans/org-domain-delete-resources-wizard.md` §5.3.
 */
const ResourceTypeSelectionStep: React.FunctionComponent<ResourceTypeSelectionStepProps> = ({
  domainName,
  types,
  inventory,
  selection,
  onToggleType,
  onToggleAll,
}) => {
  const selectAll = selectAllState(selection, inventory, types);
  const anySelectable = types.some((type) => typeState(inventory, type) === 'selectable');

  return (
    <div>
      <Content component="p" className="pf-v6-u-mb-md">
        Choose which resources Ancalagon hosts for <strong>{domainName}</strong> should be deleted. The next steps list
        what each one holds, so you can keep individual items.
      </Content>

      {/*
        `isChecked={null}` is PatternFly's indeterminate rendering, but PF 6.6.0 puts `checked: null` on
        the input, which drops React's control of the node (hence its controlled→uncontrolled warning).
        The event's `checked` would then report whatever React last left in the DOM, so the intent is
        derived from our own state instead: an indeterminate box selects everything, a full one clears.
      */}
      <Checkbox
        id="delete-resources-select-all"
        name="delete-resources-select-all"
        label="Select all resources"
        isChecked={selectAll}
        isDisabled={!anySelectable}
        onChange={() => onToggleAll(selectAll !== true)}
        className="pf-v6-u-mb-sm"
      />

      <DataList aria-label={`Resources hosted for ${domainName}`} isCompact>
        {types.map((type) => {
          const rowId = `delete-resource-${type.key}`;
          const labelId = `${rowId}-label`;
          const elements = inventory.elements.get(type.key) ?? [];
          const state = typeState(inventory, type);
          const note = blockedNote(state, type, elements);
          const summary = note ?? summarizeType(type, elements);
          return (
            <DataListItem key={type.key} id={rowId} aria-labelledby={labelId}>
              <DataListItemRow>
                <DataListCheck
                  id={`${rowId}-check`}
                  name={rowId}
                  aria-labelledby={labelId}
                  isChecked={selection.selectedTypes.has(type.key)}
                  isDisabled={state !== 'selectable'}
                  onChange={(_event, checked) => onToggleType(type.key, checked)}
                />
                <DataListItemCells
                  dataListCells={[
                    <DataListCell key="primary">
                      <span id={labelId} className="pf-v6-u-font-weight-bold">
                        {type.label}
                      </span>
                      <div className="pf-v6-u-text-color-subtle pf-v6-u-font-size-sm">{type.description}</div>
                      {summary !== null && <div className="pf-v6-u-font-size-sm pf-v6-u-mt-sm">{summary}</div>}
                    </DataListCell>,
                    <DataListCell key="status" isFilled={false} alignRight>
                      {type.status === 'under_development' && (
                        <Label isCompact color="orange">
                          Under development
                        </Label>
                      )}
                    </DataListCell>,
                  ]}
                />
              </DataListItemRow>
            </DataListItem>
          );
        })}
      </DataList>
    </div>
  );
};

export default ResourceTypeSelectionStep;
